/**
 * @author Martin Petran
 */
import {TextFieldProps} from '@mui/material'
import {ReactNode} from 'react'
import {type Control, Controller, FieldValues, type Path, RegisterOptions} from 'react-hook-form'

import {submitOnBlurAndEnterProps} from '../../utils'

import {BaseTextField} from './BaseTextField'

interface ControlledTextFieldProps<TField extends FieldValues> {
  control: Control<TField>
  formDataName: Path<TField>
  rules?: Omit<
    RegisterOptions<TField, Path<TField>>,
    'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
  >
  label: string
  children?: ReactNode
  TextFieldProps?: TextFieldProps
  transformValue?: (value: string) => string
  onSubmit?: () => Promise<void>
  onChangeCb?: () => void
  'data-test-id'?: string
}

export const ControlledTextField = <TField extends FieldValues>({
  control,
  formDataName,
  rules,
  label,
  children,
  TextFieldProps,
  transformValue,
  onSubmit,
  onChangeCb,
  'data-test-id': dataTestId
}: ControlledTextFieldProps<TField>) => (
  <Controller
    control={control}
    name={formDataName}
    rules={rules}
    render={({field: {ref, value, onChange}, fieldState: {invalid, error}}) => (
      <BaseTextField
        data-test-id={dataTestId}
        label={label}
        value={transformValue ? transformValue(value ?? '') : (value ?? '')}
        inputRef={ref}
        onChange={(e) => {
          onChange(e)
          onChangeCb?.()
        }}
        helperText={invalid && (error?.message ? error.message : 'Invalid value')}
        error={invalid}
        {...TextFieldProps}
        {...(onSubmit && submitOnBlurAndEnterProps(onSubmit))}
      >
        {children}
      </BaseTextField>
    )}
  />
)
